import React, { useMemo, useState, useEffect } from 'react'

import { Search as SearchIcon } from 'lucide-react'

import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { cn } from '@/shared/utils/helpers'

interface RemoveZonesModalProps {
	visible: boolean
	onClose: () => void
	onConfirm: (selectedZoneIds: number[]) => Promise<void>
	zones: Zone[]
	/** Number of zones that need to be removed */
	requiredRemovalCount?: number
	isLoading?: boolean
}

export const RemoveZonesModal: React.FC<RemoveZonesModalProps> = ({
	visible,
	onClose,
	onConfirm,
	zones,
	requiredRemovalCount,
	isLoading = false,
}) => {
	const { t } = useTranslation('common')
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedZoneIds, setSelectedZoneIds] = useState<Set<number>>(new Set())
	const [isConfirming, setIsConfirming] = useState(false)

	// Reset state when modal opens
	useEffect(() => {
		if (visible) {
			setSearchQuery('')
			setSelectedZoneIds(new Set())
			setIsConfirming(false)
		}
	}, [visible])

	// Filter zones based on search query
	const filteredZones = useMemo(() => {
		if (!searchQuery.trim()) {
			return zones
		}

		const query = searchQuery.toLowerCase()
		return zones.filter((zone) => {
			// Search by zone name
			if (zone.name.toLowerCase().includes(query)) {
				return true
			}

			// Search by group names
			if (
				zone.groups?.some((group) => group.name.toLowerCase().includes(query))
			) {
				return true
			}

			return false
		})
	}, [zones, searchQuery])

	const handleToggleZone = (zoneId: number) => {
		setSelectedZoneIds((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(zoneId)) {
				newSet.delete(zoneId)
			} else {
				newSet.add(zoneId)
			}
			return newSet
		})
	}

	const handleClose = () => {
		setSearchQuery('')
		setSelectedZoneIds(new Set())
		setIsConfirming(false)
		onClose()
	}

	const handleConfirm = async () => {
		if (selectedZoneIds.size === 0) return

		setIsConfirming(true)
		try {
			await onConfirm(Array.from(selectedZoneIds))
			handleClose()
		} catch (error) {
			console.error('Failed to remove zones:', error)
		} finally {
			setIsConfirming(false)
		}
	}

	const getZoneGroupsText = (zone: Zone): string | null => {
		if (!zone.groups || zone.groups.length === 0) {
			return null
		}

		const groupNames = zone.groups.map((group) => group.name).join(', ')
		return t('account.remove_zones_modal.included_in', { groups: groupNames })
	}

	const hasEnoughSelected =
		requiredRemovalCount === undefined ||
		selectedZoneIds.size >= requiredRemovalCount

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-5">
					<DialogTitle className="text-xl font-semibold">
						{t('account.remove_zones_modal.title')}
					</DialogTitle>
					{requiredRemovalCount !== undefined && requiredRemovalCount > 0 && (
						<p className="text-content-tertiary mt-1 text-sm">
							{t('account.remove_zones_modal.subtitle', {
								count: requiredRemovalCount,
							})}
						</p>
					)}
				</div>

				{/* Content */}
				<div className="px-6 pb-4 flex flex-col flex-1 overflow-hidden">
					{/* Search Input */}
					<div className="relative mb-4">
						<SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							type="search"
							placeholder={t('account.remove_zones_modal.search_placeholder')}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9 py-2.5 rounded-lg text-sm placeholder:text-muted-foreground bg-transparent"
						/>
					</div>

					{/* Zones List */}
					<div className="flex-1 overflow-y-auto -mx-6 px-6 border-t border-border">
						{isLoading ? (
							<div className="flex items-center justify-center py-12">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
							</div>
						) : filteredZones.length === 0 ? (
							<div className="flex items-center justify-center py-12 text-content-tertiary">
								{t('account.remove_zones_modal.no_zones_found')}
							</div>
						) : (
							<div className="divide-y divide-border">
								{filteredZones.map((zone) => {
									const isSelected = selectedZoneIds.has(zone.id)
									const groupsText = getZoneGroupsText(zone)

									return (
										<label
											key={zone.id}
											className={cn(
												'flex items-center justify-between py-4 cursor-pointer transition-colors',
												isSelected && 'bg-content-tertiary/5'
											)}
										>
											<div className="flex-1 min-w-0 pr-4">
												<p className="font-medium truncate">{zone.name}</p>
												{groupsText && (
													<p className="text-sm text-content-tertiary truncate mt-0.5">
														{groupsText}
													</p>
												)}
											</div>
											<Checkbox
												checked={isSelected}
												onCheckedChange={() => handleToggleZone(zone.id)}
												className="h-5 w-5 flex-shrink-0"
											/>
										</label>
									)
								})}
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="px-6 py-5 border-t flex justify-end gap-3">
					<Button
						type="button"
						variant="ghost"
						onClick={handleClose}
						disabled={isConfirming}
						className="rounded-full px-6"
					>
						{t('account.remove_zones_modal.cancel')}
					</Button>
					<Button
						type="button"
						variant="default"
						onClick={handleConfirm}
						disabled={
							selectedZoneIds.size === 0 || isConfirming || !hasEnoughSelected
						}
						className="rounded-full px-6"
					>
						{isConfirming
							? t('account.remove_zones_modal.removing')
							: t('account.remove_zones_modal.remove')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default RemoveZonesModal
